// PTOEntryModal.tsx
// Modal overlay for creating/editing supplementary PTO entries (not PE-sourced).

import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import type { PTOEntry, PTOEmployee, PTOEntryInput, EntryType } from './ptoTypes';
import { SUPPLEMENTARY_TYPES } from './ptoConstants';
import { workdaysBetween, findEntryConflict, formatDate, formatTime12, compareByName } from './ptoUtils';

interface PTOEntryModalProps {
  entry: PTOEntry | null;          // null = create mode, non-null = edit mode
  prefill?: {                      // optional initial values for create mode (ignored in edit mode)
    staffIndex?: string;
    entryType?: EntryType;
    entryDate?: string;
  } | null;
  employees: PTOEmployee[];
  existingEntries: PTOEntry[];     // all entries (PE + manual) for conflict detection
  onSave: (inputs: PTOEntryInput[]) => Promise<void>;
  onClose: () => void;
}

/** Build a human-readable label for a conflicting entry. */
function describeConflict(e: PTOEntry): string {
  const window = e.startTime && e.endTime
    ? ` (${formatTime12(e.startTime)} – ${formatTime12(e.endTime)})`
    : '';
  return `${e.entryType}${window} on ${formatDate(e.entryDate)}`;
}

// ── Styles ────────────────────────────────────────────────────────────────────

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
};

const modalStyle: React.CSSProperties = {
  background: '#151820',
  border: '1px solid var(--border)', borderRadius: 12,
  width: 480, maxWidth: '95vw',
  boxShadow: '0 8px 40px rgba(0,0,0,.6)',
};

const headerStyle: React.CSSProperties = {
  padding: '16px 20px', borderBottom: '1px solid var(--border)',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};

const bodyStyle: React.CSSProperties = {
  padding: 20, maxHeight: '70vh', overflowY: 'auto',
};

const footerStyle: React.CSSProperties = {
  padding: '14px 20px', borderTop: '1px solid var(--border)',
  display: 'flex', justifyContent: 'flex-end', gap: 10,
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.4px', color: 'var(--muted)', marginBottom: 5,
  display: 'block',
};

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 11px',
  border: '1.5px solid var(--border)', borderRadius: 6,
  background: 'rgba(255,255,255,.05)', color: 'var(--text)', fontSize: 13,
  outline: 'none',
};

// Selects need a solid background — the native dropdown panel inherits it,
// and a translucent fill renders as washed-out / unreadable on Windows.
const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: '#1a1f28',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  paddingRight: 28,
  backgroundImage:
    'url("data:image/svg+xml;utf8,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 12 12\'><path fill=\'%23a7b3c4\' d=\'M2 4l4 4 4-4\'/></svg>")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  backgroundSize: '10px 10px',
};

const optionStyle: React.CSSProperties = {
  background: '#1a1f28',
  color: 'var(--text)',
};

const fieldGroupStyle: React.CSSProperties = { marginBottom: 15 };

const hintStyle: React.CSSProperties = { fontSize: 11, color: 'var(--muted)', marginTop: 4 };

const rowStyle: React.CSSProperties = { display: 'flex', gap: 12 };

// ── Component ─────────────────────────────────────────────────────────────────

export default function PTOEntryModal({ entry, prefill, employees, existingEntries, onSave, onClose }: PTOEntryModalProps) {
  const isEdit = entry !== null;

  // Form state — entry (edit mode) wins, then prefill (pre-filled create), else blank.
  const [staffIndex, setStaffIndex] = useState(entry?.staffIndex ?? prefill?.staffIndex ?? '');
  const [entryType, setEntryType] = useState<EntryType | ''>(
    entry && SUPPLEMENTARY_TYPES.includes(entry.entryType)
      ? entry.entryType
      : (prefill?.entryType && SUPPLEMENTARY_TYPES.includes(prefill.entryType) ? prefill.entryType : ''),
  );
  const [entryDate, setEntryDate] = useState(entry?.entryDate ?? prefill?.entryDate ?? '');
  const [note, setNote] = useState(entry?.note ?? '');

  // Appointment fields
  const [startTime, setStartTime] = useState(entry?.startTime ?? '');
  const [endTime, setEndTime] = useState(entry?.endTime ?? '');

  // Flexing Off fields
  const [isFullDay, setIsFullDay] = useState<boolean>(
    entry?.entryType === 'Flexing Off' ? (entry.hours === 8 && !entry.endTime) : true,
  );
  const [leavingAt, setLeavingAt] = useState(entry?.entryType === 'Flexing Off' ? (entry.endTime ?? '') : '');

  // Friday Work fields
  const [swapPartner, setSwapPartner] = useState(entry?.swapPartner ?? '');

  // Sabbatical / multi-day fields
  const [endDate, setEndDate] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sorted employees for the dropdown (alphabetical by display name)
  const sortedEmployees = useMemo(
    () => [...employees].sort(compareByName),
    [employees],
  );

  // Entry types alphabetized for the picklist (constant array stays in source order).
  const sortedSupplementaryTypes = useMemo(
    () => [...SUPPLEMENTARY_TYPES].sort((a, b) => a.localeCompare(b)),
    [],
  );

  // Employees in the same office as the selected employee (for Friday Work swap partner)
  const sameOfficeEmployees = useMemo(() => {
    const selected = employees.find((e) => e.staffIndex === staffIndex);
    if (!selected) return [];
    return sortedEmployees.filter((e) => e.office === selected.office && e.staffIndex !== staffIndex);
  }, [employees, sortedEmployees, staffIndex]);

  // ── Validation & save ────────────────────────────────────────────────────

  async function handleSave() {
    setError(null);

    if (!staffIndex) { setError('Please select an employee.'); return; }
    if (!entryType)  { setError('Please select an entry type.'); return; }
    if (!entryDate)  { setError('Please select a date.'); return; }

    const input: PTOEntryInput = {
      staffIndex,
      entryType: entryType as EntryType,
      entryDate,
      note: note.trim() || null,
    };

    // Type-specific fields
    if (entryType === 'Appointment') {
      input.startTime = startTime || null;
      input.endTime = endTime || null;
      // Calculate hours from times if both provided
      if (startTime && endTime) {
        const [sh, sm] = startTime.split(':').map(Number);
        const [eh, em] = endTime.split(':').map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        input.hours = diff > 0 ? Math.round((diff / 60) * 100) / 100 : null;
      }
    } else if (entryType === 'Flexing Off') {
      if (isFullDay) {
        input.hours = 8;
        input.endTime = null;
      } else {
        input.endTime = leavingAt || null;
        input.hours = null;
      }
    } else if (entryType === 'Friday Work') {
      input.swapPartner = swapPartner || null;
    } else if (entryType === 'Deadline') {
      input.hours = null;
    }

    // Build the list of entries to save. Sabbatical with an end date expands
    // to one entry per workday (Mon-Fri).
    let inputs: PTOEntryInput[] = [input];
    if (entryType === 'Sabbatical' && !isEdit && endDate && endDate >= entryDate) {
      const dates = workdaysBetween(entryDate, endDate);
      if (dates.length === 0) {
        setError('No weekdays in the selected range.');
        return;
      }
      inputs = dates.map((d) => ({ ...input, entryDate: d }));
    }

    // Double-booking gate: prevent overlapping out-of-office entries for the
    // same employee on the same date.
    const excludeId = isEdit ? entry!.id : null;
    const empName = employees.find((e) => e.staffIndex === staffIndex)?.displayName ?? 'This employee';
    for (const candidate of inputs) {
      const conflict = findEntryConflict(candidate, staffIndex, existingEntries, excludeId);
      if (conflict) {
        setError(`${empName} already has ${describeConflict(conflict)}.`);
        return;
      }
    }

    setSaving(true);
    try {
      await onSave(inputs);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'Failed to save entry.');
    } finally {
      setSaving(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={overlayStyle} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>

        {/* Header */}
        <div style={headerStyle}>
          <div style={{ fontSize: 15, fontWeight: 700 }}>
            {isEdit ? 'Edit Entry' : 'Add Entry'}
          </div>
          <button className="btn" style={{ padding: '4px 8px' }} onClick={onClose}>
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>

          {/* Employee */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Employee</label>
            <select
              style={selectStyle}
              value={staffIndex}
              onChange={(e) => setStaffIndex(e.target.value)}
            >
              <option style={optionStyle} value="">Select employee...</option>
              {sortedEmployees.map((emp) => (
                <option style={optionStyle} key={emp.staffIndex} value={emp.staffIndex}>
                  {emp.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Entry Type */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Entry Type</label>
            <select
              style={selectStyle}
              value={entryType}
              onChange={(e) => setEntryType(e.target.value as EntryType | '')}
            >
              <option style={optionStyle} value="">Select type...</option>
              {sortedSupplementaryTypes.map((t) => (
                <option style={optionStyle} key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>

          {/* Date (start date when Sabbatical multi-day) */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>
              {entryType === 'Sabbatical' && !isEdit ? 'Start Date' : 'Date'}
            </label>
            <input
              type="date"
              style={inputStyle}
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
            />
          </div>

          {/* Sabbatical: optional end date for multi-day range */}
          {entryType === 'Sabbatical' && !isEdit && (
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>End Date</label>
              <input
                type="date"
                style={inputStyle}
                value={endDate}
                min={entryDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
              <div style={hintStyle}>
                Leave blank for a single day. Otherwise one entry is created per
                weekday in the range.
              </div>
            </div>
          )}

          {/* ── Conditional fields ────────────────────────────────────────── */}

          {/* Appointment: Start time + End time */}
          {entryType === 'Appointment' && (
            <div style={fieldGroupStyle}>
              <div style={rowStyle}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Start Time</label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                  />
                  <div style={hintStyle}>e.g. 01:00 PM</div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>End Time</label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                  />
                  <div style={hintStyle}>e.g. 03:00 PM</div>
                </div>
              </div>
            </div>
          )}

          {/* Friday Work: Swap partner */}
          {entryType === 'Friday Work' && (
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Swap Partner</label>
              <select
                style={selectStyle}
                value={swapPartner}
                onChange={(e) => setSwapPartner(e.target.value)}
              >
                <option style={optionStyle} value="">None (optional)</option>
                {sameOfficeEmployees.map((emp) => (
                  <option style={optionStyle} key={emp.staffIndex} value={emp.staffIndex}>
                    {emp.displayName}
                  </option>
                ))}
              </select>
              <div style={hintStyle}>Employees in the same office</div>
            </div>
          )}

          {/* Flexing Off: Full-day checkbox + optional leaving-at time */}
          {entryType === 'Flexing Off' && (
            <div style={fieldGroupStyle}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: 10 }}>
                <input
                  type="checkbox"
                  checked={isFullDay}
                  onChange={(e) => setIsFullDay(e.target.checked)}
                />
                <span style={{ fontSize: 13 }}>Full day (8 hours)</span>
              </label>
              {!isFullDay && (
                <div>
                  <label style={labelStyle}>Leaving At</label>
                  <input
                    type="time"
                    style={inputStyle}
                    value={leavingAt}
                    onChange={(e) => setLeavingAt(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div style={fieldGroupStyle}>
            <label style={labelStyle}>Notes</label>
            <input
              type="text"
              style={inputStyle}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional note..."
            />
          </div>

          {/* Error message */}
          {error && (
            <div style={{ fontSize: 12, color: 'var(--bad)', marginTop: 6 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Entry'}
          </button>
        </div>

      </div>
    </div>
  );
}
